import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Save, Lock, Mail, Phone, Building2, Briefcase } from 'lucide-react';
import api from '../services/api';
import './Profile.css';

function Profile() {
  const { t } = useTranslation(['profile', 'common']);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    middle_name: '',
    email: '',
    phone: '',
    position: '',
  });

  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await api.getProfile();
      const p = res.data;
      setProfile(p);
      setForm({
        first_name: p.first_name || '',
        last_name: p.last_name || '',
        middle_name: p.middle_name || '',
        email: p.email || '',
        phone: p.phone || '',
        position: p.position || '',
      });
      setLoading(false);
    } catch (err) {
      console.error('Ошибка загрузки профиля:', err);
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.updateProfile(form);
      setProfile(res.data);
      // Обновляем localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.first_name = res.data.first_name;
      user.last_name = res.data.last_name;
      localStorage.setItem('user', JSON.stringify(user));
      alert(t('profile:profileSaved'));
    } catch (err) {
      alert('Ошибка: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert(t('profile:passwordMismatch'));
      return;
    }
    if (passwordForm.new_password.length < 6) {
      alert(t('profile:passwordTooShort'));
      return;
    }
    setChangingPassword(true);
    try {
      await api.changePassword({
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password,
      });
      alert(t('profile:passwordChanged'));
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      const detail = err.response?.data;
      if (typeof detail === 'object') {
        const msg = Object.entries(detail).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n');
        alert('Ошибка:\n' + msg);
      } else {
        alert('Ошибка: ' + (detail || err.message));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <div className="profile-page"><div className="loading-spinner">{t('common:table.loading')}</div></div>;
  }

  return (
    <div className="profile-page">
      <h1><User size={24} /> {t('profile:title')}</h1>

      {/* Инфо-карточка */}
      <div className="profile-info-card">
        <div className="profile-avatar">
          {(profile?.last_name || profile?.username || '?')[0].toUpperCase()}
        </div>
        <div className="profile-info">
          <h2>{profile?.last_name} {profile?.first_name} {profile?.middle_name}</h2>
          <div className="profile-meta">
            <span className={`role-badge role-${profile?.role}`}>
              {t(`common:roles.${profile?.role}`, { defaultValue: profile?.role })}
            </span>
            {profile?.department_name && (
              <span className="meta-item">
                <Building2 size={14} /> {profile.department_name}
              </span>
            )}
            {profile?.position && (
              <span className="meta-item">
                <Briefcase size={14} /> {profile.position}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Редактирование профиля */}
      <form onSubmit={handleSaveProfile} className="form-card">
        <h2>{t('profile:personalInfo')}</h2>

        <div className="form-row form-row-3">
          <div className="form-group">
            <label>{t('profile:lastName')}</label>
            <input type="text" className="form-control" value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label>{t('profile:firstName')}</label>
            <input type="text" className="form-control" value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label>{t('profile:middleName')}</label>
            <input type="text" className="form-control" value={form.middle_name}
              onChange={(e) => setForm({ ...form, middle_name: e.target.value })} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label><Mail size={14} /> {t('profile:email')}</label>
            <input type="email" className="form-control" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="form-group">
            <label><Phone size={14} /> {t('profile:phone')}</label>
            <input type="tel" className="form-control" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>

        <div className="form-group">
          <label><Briefcase size={14} /> {t('profile:position')}</label>
          <input type="text" className="form-control" value={form.position}
            onChange={(e) => setForm({ ...form, position: e.target.value })} />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            <Save size={18} /> {saving ? t('common:table.loading') : t('profile:saveProfile')}
          </button>
        </div>
      </form>

      {/* Смена пароля */}
      <form onSubmit={handleChangePassword} className="form-card">
        <h2><Lock size={18} /> {t('profile:changePassword')}</h2>

        <div className="form-group">
          <label>{t('profile:currentPassword')}</label>
          <input type="password" className="form-control" value={passwordForm.old_password}
            onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })} required />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>{t('profile:newPassword')}</label>
            <input type="password" className="form-control" value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>{t('profile:confirmPassword')}</label>
            <input type="password" className="form-control" value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} required />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-warning" disabled={changingPassword}>
            <Lock size={18} /> {changingPassword ? t('common:table.loading') : t('profile:savePassword')}
          </button>
        </div>
      </form>

      <NotificationPreferences />
    </div>
  );
}

function NotificationPreferences() {
  const [prefs, setPrefs] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [testing, setTesting] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await api.getNotificationPreferences();
        setPrefs(res.data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const save = async (patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
    try {
      await api.updateNotificationPreferences(patch);
    } catch (e) {
      alert('Не удалось сохранить: ' + (e.response?.data?.detail || e.message));
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await api.sendTestEmail();
      alert(res.data.ok ? '✓ Тестовое письмо отправлено на ' + prefs.user_email : '❌ Ошибка: ' + res.data.message);
    } catch (e) {
      alert('Ошибка: ' + (e.response?.data?.detail || e.message));
    }
    setTesting(false);
  };

  if (loading) return null;
  if (!prefs) return null;

  return (
    <div style={{ marginTop: 24, padding: 20, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
      <h3 style={{ marginTop: 0 }}>📧 Email-уведомления</h3>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
        Ваш email: <strong>{prefs.user_email || '— не указан —'}</strong>
        {!prefs.user_email && (
          <span style={{ color: '#b91c1c', marginLeft: 8 }}>
            Укажите email в профиле выше, иначе получать письма невозможно.
          </span>
        )}
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={!!prefs.email_enabled}
          onChange={(e) => save({ email_enabled: e.target.checked })}
          disabled={!prefs.user_email}
        />
        <span>Присылать копию системных уведомлений на email</span>
      </label>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 13, marginRight: 8 }}>Минимальный приоритет для email:</label>
        <select
          value={prefs.email_min_priority || 'normal'}
          onChange={(e) => save({ email_min_priority: e.target.value })}
          style={{ padding: '4px 8px', borderRadius: 4 }}
        >
          <option value="low">Низкий (все уведомления)</option>
          <option value="normal">Обычный</option>
          <option value="high">Высокий</option>
          <option value="critical">Только критические</option>
        </select>
      </div>

      <button
        onClick={sendTest}
        disabled={testing || !prefs.user_email}
        style={{
          padding: '8px 14px',
          background: '#2563eb',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: prefs.user_email ? 'pointer' : 'not-allowed',
          opacity: prefs.user_email ? 1 : 0.5,
        }}
      >
        {testing ? 'Отправка…' : '✉ Отправить тестовое письмо'}
      </button>
    </div>
  );
}

export default Profile;
